import { and, ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import {
  InformationCircleOutlinedIcon,
  InputAdornment,
  MenuItem,
  Select,
  styled,
  TextField,
  Tooltip,
} from 'opensolar-ui'
import React, { HTMLInputTypeAttribute, useMemo } from 'react'
import { formatCurrencyNumber } from 'util/misc'

const tester = rankWith(100, and(uiTypeIs('OSInputControl')))

type SelectOption = {
  value: string
  label: string
}

const InputRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  gap: '8px',
}))

const TooltipWrapper = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  marginBottom: '14px',
}))

const OSInputControlRenderer: React.FC<ControlProps> = (props) => {
  const selectOptions = useMemo(() => {
    let options: SelectOption[] | null = null
    if (props.schema?.oneOf?.length) {
      options = props.schema?.oneOf.map((item) => ({ value: item.const, label: item.title }))
    }
    if (props.schema?.enum?.length) {
      options = props.schema?.enum.map((item) => ({ value: item, label: item }))
    }
    if (options?.length) {
      options.unshift({ value: '', label: '' })
    }
    return options
  }, [props.schema?.oneOf, props.schema?.enum])

  const formattedValue = useMemo(() => {
    // if a field is disabled and set to be currency we will format the value as currency
    // only doing this for disabled fields for now to avoid formatting the value as the user changes it
    // which is not a requirement yet
    if (props.data && props.schema?.type === 'string' && props.uischema?.options?.isCurrency && !props.enabled) {
      try {
        return formatCurrencyNumber(props.data, 2)
      } catch (error) {
        return props.data
      }
    }
    return props.data
  }, [props.data, props.schema?.type, props.uischema?.options?.isCurrency])

  if (selectOptions?.length) {
    return (
      <Select
        value={props.data || ''}
        style={{ minWidth: '200px' }}
        onChange={(ev) => props.handleChange(props.path, ev.target.value)}
        label={typeof props.label === 'string' ? props.label : props.label.default}
        fullWidth
        disabled={!props.enabled}
        variant="outlined"
        margin="dense"
      >
        {selectOptions?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    )
  } else
    return (
      <InputRow>
        <TextField
          type={props.schema?.type as HTMLInputTypeAttribute}
          style={{ minWidth: '200px' }}
          value={formattedValue}
          onChange={(ev) => {
            const value = props.schema?.type === 'number' ? parseFloat(ev.target.value) : ev.target.value
            props.handleChange(props.path, value)
          }}
          label={typeof props.label === 'string' ? props.label : props.label.default}
          fullWidth
          disabled={!props.enabled}
          variant="outlined"
          size="small"
          margin="dense"
          inputProps={{
            max: props.schema.maximum ? props.schema.maximum : undefined,
            min: props.schema.minimum ? props.schema.minimum : undefined,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            startAdornment:
              props.uischema?.options?.isCurrency && props.uischema?.options?.currencySymbol ? (
                <InputAdornment position="start">{props.uischema?.options?.currencySymbol}</InputAdornment>
              ) : undefined,
          }}
        />
        {props.uischema?.options?.tooltip && (
          <TooltipWrapper>
            <Tooltip title={props.uischema?.options?.tooltip}>
              <div>
                <InformationCircleOutlinedIcon />
              </div>
            </Tooltip>
          </TooltipWrapper>
        )}
      </InputRow>
    )
}

const renderer = withJsonFormsControlProps(OSInputControlRenderer)

export default {
  tester,
  renderer,
}